import { profileMenu } from "../data/menu.js";
import { formatOutput } from "../dsl/format-catalog.js";
import { isFrozenDesign, resolveMenuFormat, resolveTemplateVariant, } from "../dsl/template.js";
import { createPrng } from "../random/prng.js";
import { reflowPlansToFit } from "./fit-items.js";
import { balanceSpreadPlans, paginateForFormat } from "./paginate.js";
import { renderNode } from "./render-nodes.js";
import { resolveTokens } from "./resolve-tokens.js";
import { selectBlueprint } from "./select-blueprint.js";
import { renderFrozenBook } from "./render-frozen.js";
/**
 * The whole pipeline: menu plus template in, finished pages out.
 *
 * Runs identically on the server and in the browser, which is the only reason
 * the constructor preview can be trusted to match what a guest sees.
 */
export function renderBook(input) {
    if (isFrozenDesign(input.template)) {
        return renderFrozenBook(input);
    }
    const startedAt = Date.now();
    const { instanceId, menu } = input;
    const template = resolveTemplateVariant(input.template, input.variantId);
    const seed = input.seed ?? instanceId;
    // Pagination converts this instant to the restaurant's wall clock once.
    const now = input.now ?? new Date();
    const prng = createPrng(seed);
    const diagnostics = [];
    const profile = profileMenu(menu);
    const brand = mergeBrand(menu.restaurant.brand, input.brandOverrides);
    const tokenResolution = resolveTokens(template, brand);
    diagnostics.push(...tokenResolution.diagnostics);
    const pagination = paginateForFormat(menu, profile, template, prng.fork('paginate'), now, tokenResolution.tokens);
    diagnostics.push(...pagination.diagnostics);
    // Count-based pagination can still overfill a photo grid / tight list pocket.
    // Reflow splits those pages so dishes are not clipped by overflow:hidden.
    const fitted = reflowPlansToFit(pagination.plans, template, tokenResolution.tokens, profile.categories, prng, input.blueprintOverride);
    diagnostics.push(...fitted.diagnostics);
    // Reflow can change the page count; re-balance spreads so we never leave an
    // unpaired left leaf after splits / pull-forward.
    const plans = balanceSpreadPlans(fitted.plans, template, template.flow, diagnostics);
    const contents = buildContents(plans);
    const assets = new Map(template.assets.map((asset) => [asset.id, asset.url]));
    const pages = [];
    const selections = [];
    const history = [];
    plans.forEach((plan, index) => {
        const side = sideForIndex(index, template.canvas.spread);
        const selection = selectBlueprint(template, {
            plan,
            profile: plan.category ? profile.categories[plan.category.id] : undefined,
            side,
            history: history.slice(-Math.max(1, template.flow.varietyWindow)),
            pageIndex: index,
        }, prng.fork(`select:${index}`));
        const blueprint = resolveBlueprint(template, plan, selection.blueprint, input.blueprintOverride);
        if (!blueprint) {
            diagnostics.push({
                level: 'error',
                code: 'no-blueprint-match',
                message: `No "${plan.group}" layout available for page ${index + 1}`,
                pageIndex: index,
            });
            pages.push(emptyPage(index, side, plan, template, tokenResolution.tokens));
            return;
        }
        if (!selection.blueprint) {
            diagnostics.push({
                level: 'warning',
                code: 'no-blueprint-match',
                message: `Page ${index + 1} fell back to "${blueprint.label}" because no ${plan.group} layout satisfied its constraints`,
                pageIndex: index,
            });
        }
        history.push(blueprint.variant);
        const itemIds = [];
        const context = {
            tokens: tokenResolution.tokens,
            scope: buildScope(menu, plan, profile, index, side, contents, plans.length),
            prng: prng.fork(`page:${index}:${blueprint.id}`),
            diagnostics,
            pageIndex: index,
            pageGroup: plan.group,
            menuFormat: resolveMenuFormat(template),
            currency: menu.restaurant.currency,
            locale: menu.restaurant.locale,
            assets,
            availableWidth: template.canvas.width - template.canvas.safeArea * 2,
            itemIds,
            showHiddenNodes: input.showHiddenNodes,
            ghostEmptyRepeaters: input.ghostEmptyRepeaters,
        };
        const body = renderNode(blueprint.root, context);
        pages.push({
            id: `${instanceId}:${index}`,
            index,
            side,
            group: plan.group,
            blueprintId: blueprint.id,
            variant: blueprint.variant,
            categoryId: plan.category?.id,
            title: plan.title,
            itemIds,
            width: template.canvas.width,
            height: template.canvas.height,
            root: pageFrame(index, template, tokenResolution.tokens, body ? [body] : []),
        });
        selections.push({
            pageIndex: index,
            blueprintId: blueprint.id,
            variant: blueprint.variant,
            score: selection.score,
            reasoning: selection.reasoning,
            rejected: selection.rejected,
        });
    });
    if (input.blueprintOverride) {
        const forcedId = input.blueprintOverride.blueprintId;
        if (!pages.some((page) => page.blueprintId === forcedId)) {
            const blueprint = template.blueprints.find((entry) => entry.id === forcedId);
            if (blueprint) {
                const plan = previewPlanForGroup(blueprint.group, menu);
                const index = pages.length;
                const side = sideForIndex(index, template.canvas.spread);
                const itemIds = [];
                const context = {
                    tokens: tokenResolution.tokens,
                    scope: buildScope(menu, plan, profile, index, side, contents, Math.max(1, pages.length)),
                    prng: prng.fork(`preview:${blueprint.id}`),
                    diagnostics,
                    pageIndex: index,
                    pageGroup: blueprint.group,
                    menuFormat: resolveMenuFormat(template),
                    currency: menu.restaurant.currency,
                    locale: menu.restaurant.locale,
                    assets,
                    availableWidth: template.canvas.width - template.canvas.safeArea * 2,
                    itemIds,
                    showHiddenNodes: input.showHiddenNodes,
                    ghostEmptyRepeaters: true,
                };
                const body = renderNode(blueprint.root, context);
                pages.push({
                    id: `${instanceId}:preview:${blueprint.id}`,
                    index,
                    side,
                    group: blueprint.group,
                    blueprintId: blueprint.id,
                    variant: blueprint.variant,
                    categoryId: plan.category?.id,
                    title: plan.title,
                    itemIds,
                    width: template.canvas.width,
                    height: template.canvas.height,
                    root: pageFrame(index, template, tokenResolution.tokens, body ? [body] : []),
                });
            }
        }
    }
    return {
        instanceId,
        templateVersionId: template.id,
        variantId: input.variantId,
        format: resolveMenuFormat(template),
        output: formatOutput(resolveMenuFormat(template)),
        seed,
        canvas: template.canvas,
        tokens: tokenResolution.tokens,
        fonts: Object.values(tokenResolution.tokens.fonts),
        pages,
        diagnostics,
        profile,
        selections,
        elapsedMs: Date.now() - startedAt,
    };
}
/**
 * Page 0 is a recto, as it would be in a physical book.
 *
 * Getting this wrong is subtle and very visible: the cover ends up on the left
 * of a spread and every subsequent facing pair is off by one.
 */
function sideForIndex(index, spread) {
    if (!spread)
        return 'right';
    return index % 2 === 0 ? 'right' : 'left';
}
function mergeBrand(base, overrides) {
    if (!overrides)
        return base;
    return {
        ...base,
        ...stripUndefined(overrides),
        colorOverrides: { ...base.colorOverrides, ...overrides.colorOverrides },
    };
}
function stripUndefined(value) {
    return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined));
}
function resolveBlueprint(template, plan, selected, override) {
    if (override && override.group === plan.group) {
        const forced = template.blueprints.find((blueprint) => blueprint.id === override.blueprintId);
        if (forced)
            return forced;
    }
    if (selected)
        return selected;
    // Constraints all failed. Showing the closest layout with some overflow beats
    // showing a blank page, and the diagnostic tells the author what happened.
    const sameGroup = template.blueprints.filter((blueprint) => blueprint.group === plan.group);
    if (sameGroup.length === 0)
        return null;
    return sameGroup.reduce((best, blueprint) => capacityDistance(blueprint, plan) < capacityDistance(best, plan) ? blueprint : best);
}
function capacityDistance(blueprint, plan) {
    if (!blueprint.capacity)
        return Number.MAX_SAFE_INTEGER - blueprint.weight;
    return Math.abs(blueprint.capacity.preferred - plan.items.length);
}
/**
 * Table-of-contents entries.
 *
 * Built from the plans rather than the finished pages because every plan
 * becomes exactly one page; that lets the contents page render with correct
 * numbers in a single pass instead of needing a second render.
 */
function previewPlanForGroup(group, menu) {
    const category = menu.categories[0];
    const items = category?.items.slice(0, 8) ?? [];
    const featured = menu.categories.flatMap((entry) => entry.items).find((item) => item.featured) ?? items[0];
    return {
        group,
        category: group === 'category' || group === 'divider' || group === 'spotlight' ? category : undefined,
        items: group === 'category' ? items : [],
        pageInCategory: 0,
        pagesInCategory: 1,
        spotlightItem: group === 'spotlight' ? featured : undefined,
        title: category?.name,
    };
}
function buildContents(plans) {
    const entries = [];
    const seen = new Set();
    plans.forEach((plan, index) => {
        if (plan.group !== 'category' || !plan.category)
            return;
        if (seen.has(plan.category.id))
            return;
        seen.add(plan.category.id);
        entries.push({ title: plan.category.name, categoryId: plan.category.id, page: index + 1 });
    });
    return entries;
}
function buildScope(menu, plan, profile, index, side, contents, pageCount) {
    const categoryProfile = plan.category ? profile.categories[plan.category.id] : undefined;
    const categories = [...menu.categories]
        .sort((a, b) => a.position - b.position)
        .map((category) => {
        const entryProfile = profile.categories[category.id];
        return {
            ...category,
            itemCount: entryProfile?.itemCount ?? category.items.length,
            imageCoverage: entryProfile?.imageCoverage ?? 0,
        };
    });
    return {
        menu: {
            id: menu.id,
            title: menu.title,
            subtitle: menu.subtitle,
            footnote: menu.footnote,
            categoryCount: menu.categories.length,
            categories,
        },
        restaurant: menu.restaurant,
        brand: menu.restaurant.brand,
        category: plan.category
            ? {
                ...plan.category,
                // Cross-category fill may set plan.title to "A · B" — surface that
                // in {{category.name}} bindings so the page header matches the dishes.
                name: plan.title ?? plan.category.name,
                itemCount: categoryProfile?.itemCount ?? plan.category.items.length,
                imageCoverage: categoryProfile?.imageCoverage ?? 0,
            }
            : undefined,
        // `item` is bound here only for spotlight pages; on a category page the
        // repeater rebinds it per entry.
        item: plan.spotlightItem,
        page: {
            index,
            number: index + 1,
            side,
            group: plan.group,
            title: plan.title,
            items: plan.items,
            itemCount: plan.items.length,
            pageInCategory: plan.pageInCategory,
            categoryPage: plan.pageInCategory + 1,
            pagesInCategory: plan.pagesInCategory,
            isFirstOfCategory: plan.pageInCategory === 0,
            isLastOfCategory: plan.pageInCategory === plan.pagesInCategory - 1,
        },
        book: { pageCount, contents },
        profile,
    };
}
/** The fixed-size box every blueprint renders into. */
function pageFrame(index, template, tokens, children) {
    const { canvas } = template;
    return {
        id: `page-${index}`,
        tag: 'box',
        style: {
            position: 'relative',
            width: `${canvas.width}px`,
            height: `${canvas.height}px`,
            overflow: 'hidden',
            backgroundColor: tokens.colors.surface ?? '#ffffff',
            color: tokens.colors.onSurface ?? '#111111',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
        },
        children,
    };
}
function emptyPage(index, side, plan, template, tokens) {
    return {
        id: `blank:${index}`,
        index,
        side,
        group: plan.group,
        blueprintId: 'none',
        variant: 'none',
        categoryId: plan.category?.id,
        title: plan.title,
        itemIds: [],
        width: template.canvas.width,
        height: template.canvas.height,
        root: pageFrame(index, template, tokens, []),
    };
}
//# sourceMappingURL=render-book.js.map